/**
 * Text formatting and manipulation utilities
 *
 * @remarks
 * Provides comprehensive text formatting functions for values, currency, phone numbers, durations, names, and text transformations.
 * Handles conversion between different formats (decimal hours to duration strings, seconds to hours, newlines to HTML, etc.)
 * and ensures consistent output formatting across the application.
 *
 * @public
 * @useWhen Formatting values for display; Converting durations and time formats; Formatting phone numbers and names; Transforming text for HTML display; Escaping untrusted text before it is concatenated into markup
 */

import { i18nInstance } from 'src/CrudManager/Core/Utils/i18n'

/**
 * Formats various value types to display strings
 *
 * Converts booleans to French "Oui"/"Non", objects to JSON strings, and other types to strings.
 * Returns empty string for null/undefined values to ensure consistent display behavior.
 *
 * @param value - The value to format (can be any type)
 * @returns Formatted string representation of the value, or empty string for null/undefined
 * @public
 * @example
 * ```typescript
 * formatValue(true) // Returns "Oui"
 * formatValue(false) // Returns "Non"
 * formatValue({ name: "John" }) // Returns JSON string
 * formatValue(null) // Returns ""
 * ```
 */
export const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return ''

  switch (typeof value) {
    case 'boolean':
      return value ? 'Oui' : 'Non'
    case 'object':
      try {
        return JSON.stringify(value, null, 2)
      } catch {
        return String(value)
      }
    default:
      return String(value)
  }
}

/**
 * Formats numbers as currency with 2 decimal places
 *
 * Converts string numbers to floats and ensures consistent decimal formatting.
 * Returns "0.00" for invalid or NaN values to prevent display errors.
 *
 * @param price - The price value to format (number or string)
 * @returns Formatted price string with 2 decimal places, or "0.00" for invalid values
 * @public
 * @example
 * ```typescript
 * formatPrice(123.456) // Returns "123.46"
 * formatPrice("99.9") // Returns "99.90"
 * formatPrice("invalid") // Returns "0.00"
 * ```
 */
export const formatPrice = (price: unknown): string => {
  if (typeof price === 'string') {
    price = parseFloat(price)
  }
  if (typeof price !== 'number' || isNaN(price)) {
    return '0.00'
  }
  return price.toFixed(2)
}

/**
 * The BCP-47 tag the UI locale maps to.
 *
 * Both faces of this product are Canadian: `fr` reads `35 728,00 $` and
 * `9 janv. 2026`, `en` reads `$35,728.00`. A bare `toLocaleDateString()` or a
 * hardcoded `en-US` prints the US order instead, which is how a French screen
 * ends up showing `9/1/2026`.
 */
export const localeFor = (uiLocale: string): string => (uiLocale.startsWith('en') ? 'en-CA' : 'fr-CA')

/** The tag for whatever locale the app is currently rendering in. */
export const currentLocaleTag = (): string => localeFor(String(i18nInstance.global.locale.value))

/**
 * Currency as the reader expects to see it.
 *
 * `formatPrice` above is the bare two-decimal form some inputs and CSV columns
 * still want; anything a user reads goes through here instead, or the amount
 * comes out `$35728.00` on a French-Canadian screen.
 */
export const formatMoney = (value: unknown, locale: string = currentLocaleTag(), currency = 'CAD'): string => {
  const n = typeof value === 'string' ? parseFloat(value) : Number(value ?? 0)

  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(Number.isFinite(n) ? n : 0)
}

/**
 * The number a hand typed, whichever hand it was.
 *
 * Accepts what either locale produces: "1 234,56", "1234.56" and the English
 * "1,234.56" all parse. The separator that appears last is the decimal one;
 * every other comma or space is a thousands mark and drops out. Returns NaN for
 * anything that is not a number, so callers can tell "empty or wrong" from zero.
 */
export const parseAmount = (raw: string): number => {
  const stripped = raw.replace(/[\s\u00a0$]/g, '')
  if (stripped === '') return NaN
  if (!/^-?[\d.,]*$/.test(stripped)) return NaN

  const lastComma = stripped.lastIndexOf(',')
  const lastDot = stripped.lastIndexOf('.')
  const decimalAt = Math.max(lastComma, lastDot)
  // one separator with 3 digits behind it is a thousands mark, not cents
  const isDecimal = decimalAt >= 0 && stripped.length - decimalAt - 1 !== 3
  const cleaned = isDecimal ? `${stripped.slice(0, decimalAt).replace(/[.,]/g, '')}.${stripped.slice(decimalAt + 1)}` : stripped.replace(/[.,]/g, '')

  return /^-?\d*\.?\d*$/.test(cleaned) ? parseFloat(cleaned) : NaN
}

/**
 * An amount as a money field should carry it: always two decimals, in the
 * reader's own separators, without the currency symbol the field's own column
 * header already names. `48` reads `48,00` on a French screen, `48.00` on an
 * English one.
 */
export const formatAmountField = (value: unknown, locale: string = currentLocaleTag()): string => {
  const n = typeof value === 'string' ? parseAmount(value) : Number(value ?? 0)

  return new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number.isFinite(n) ? n : 0)
}

/** A date in the reader's locale, `9 janv. 2026` by default. */
export const formatDate = (
  value: string | number | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' },
  locale: string = currentLocaleTag(),
): string => {
  if (value === null || value === undefined || value === '') return ''
  const date = value instanceof Date ? value : new Date(value)

  return Number.isNaN(date.getTime()) ? '' : new Intl.DateTimeFormat(locale, options).format(date)
}

/** Calendar dates retain their written day even when Laravel serializes midnight UTC. */
export const formatCalendarDate = (value: string, locale: string = currentLocaleTag()): string => {
  const day = /^\d{4}-\d{2}-\d{2}/.exec(value)?.[0]
  if (!day) return ''
  return formatDate(day, { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'UTC' }, locale)
}

/**
 * Formats phone numbers to Quebec standard format
 *
 * Handles 10-digit (local) and 11-digit (toll-free) numbers.
 * Formats as: "XXX XXX-XXXX" for local or "1 XXX XXX-XXXX" for toll-free.
 * Returns original string if format is invalid to preserve user input.
 *
 * @param phone - The phone number to format (string or number)
 * @returns Formatted phone number string or original value if invalid
 * @public
 * @example
 * ```typescript
 * formatPhoneNumber("5141234567") // Returns "514 123-4567"
 * formatPhoneNumber("15141234567") // Returns "1 514 123-4567"
 * formatPhoneNumber("123") // Returns "123" (invalid, returns as-is)
 * ```
 */
export const formatPhoneNumber = (phone: unknown): string => {
  if (!phone) return ''

  // Convert to string and remove all non-digits
  const digits = String(phone).replace(/\D/g, '')

  // Check if it's a valid phone number (10 or 11 digits)
  if (digits.length < 10 || digits.length > 11) return String(phone)

  // If 11 digits, check if it starts with 1
  if (digits.length === 11 && digits[0] !== '1') return String(phone)

  // Format the number according to Quebec standards
  const parts: string[] = []
  if (digits.length === 11) {
    parts.push('1') // Toll-free prefix
    parts.push(digits.slice(1, 4)) // Area code
    parts.push(digits.slice(4, 7)) // Exchange
    parts.push(digits.slice(7)) // Line number
    return `${parts[0]} ${parts[1]} ${parts[2]}-${parts[3]}`
  } else {
    parts.push(digits.slice(0, 3)) // Area code
    parts.push(digits.slice(3, 6)) // Exchange
    parts.push(digits.slice(6)) // Line number
    return `${parts[0]} ${parts[1]}-${parts[2]}`
  }
}

/**
 * Splits a raw phone entry into its base number and an optional extension.
 *
 * The extension is the trailing digits after an explicit marker
 * (poste / post / ext / extension / x / #), so a bare separator can never be
 * mistaken for an extension. Used by both formatting and validation so they
 * agree on where the dialable number ends.
 *
 * @param raw - The raw phone entry (may include an extension)
 * @returns The base substring, its digits only, and the extension digits ('' if none)
 * @public
 * @example
 * ```typescript
 * parsePhoneParts("514 123-4567, poste 150") // { base: "514 123-4567", baseDigits: "5141234567", ext: "150" }
 * parsePhoneParts("5141234567") // { base: "5141234567", baseDigits: "5141234567", ext: "" }
 * ```
 */
export const parsePhoneParts = (raw: unknown): { base: string; baseDigits: string; ext: string } => {
  const trimmed = String(raw ?? '').trim()
  const match = trimmed.match(/(?:poste|extension|ext\.?|post|x|#)\s*[:.]?\s*(\d+)\s*$/i)
  const ext = match?.[1] ?? ''
  // Drop the separator left between the number and the extension marker (e.g. the
  // ", " in "514 123-4567, poste 150") so the base is clean.
  const base = match ? trimmed.slice(0, match.index).replace(/[\s,;]+$/, '') : trimmed

  return { base, baseDigits: base.replace(/\D+/g, ''), ext }
}

/**
 * Formats a phone entry to the OQLF notation, preserving an optional extension
 * as ", poste NNN" (e.g. "514 123-4567, poste 150", "1 514 123-4567").
 *
 * A base that is not a recognisable 10/11-digit NANP number is returned trimmed
 * but otherwise untouched, so partial input is surfaced to validation instead of
 * being silently mangled.
 *
 * @param raw - The raw phone entry (may include an extension)
 * @returns The OQLF-formatted phone string
 * @public
 * @example
 * ```typescript
 * formatOqlfPhone("5141234567 ext 150") // Returns "514 123-4567, poste 150"
 * formatOqlfPhone("15141234567") // Returns "1 514 123-4567"
 * formatOqlfPhone("123") // Returns "123" (invalid base, returned as-is)
 * ```
 */
export const formatOqlfPhone = (raw: unknown): string => {
  const { baseDigits, ext } = parsePhoneParts(raw)

  if (baseDigits.length !== 10 && baseDigits.length !== 11) {
    return String(raw ?? '').trim()
  }

  const formatted = formatPhoneNumber(baseDigits)

  return ext ? `${formatted}, poste ${ext}` : formatted
}

/**
 * Converts newlines to HTML line breaks
 *
 * Replaces all newline characters (CRLF, LF, CR) with HTML `<br>` tags.
 * Useful for displaying multi-line text in HTML contexts where newlines are not preserved.
 *
 * @param text - The text string to convert
 * @returns Text string with newlines replaced by `<br>` tags
 * @public
 * @example
 * ```typescript
 * nl2br("Line 1\nLine 2") // Returns "Line 1<br>Line 2"
 * ```
 */
export const nl2br = (text: string): string => {
  return text.replace(/([^\n\r>]?)(\r\n|\n\r|\r|\n)/g, '$1<br>')
}

/**
 * Escapes text for safe interpolation into an HTML string
 *
 * Replaces the five characters that can end an attribute or open a tag. Anything
 * a user or a third-party API wrote must go through this before it is concatenated
 * into markup — a Quasar `Dialog` with `html: true` renders its message as markup,
 * so an unescaped note or name is executed rather than displayed.
 *
 * @param value - The value to escape; non-strings are stringified, nullish becomes empty
 * @returns The value with `&`, `<`, `>`, `"` and `'` replaced by their entities
 * @public
 * @example
 * ```typescript
 * escapeHtml('<img src=x onerror=alert(1)>') // Returns "&lt;img src=x onerror=alert(1)&gt;"
 * ```
 */
export const escapeHtml = (value: unknown): string => {
  if (value === null || value === undefined) return ''

  return String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;')
}

/**
 * Converts decimal hours to formatted duration string
 *
 * Formats decimal hours as "XhYY" format (e.g., 2.5 hours becomes "2h30").
 * Handles negative values by prefixing with minus sign.
 * Rounds minutes to nearest minute increment for consistent display.
 *
 * @param number - Decimal hours to convert (e.g., 2.5)
 * @returns Formatted duration string (e.g., "2h30" or "-2h30")
 * @public
 * @example
 * ```typescript
 * convertDuration(2.5) // Returns "2h30"
 * convertDuration(1.25) // Returns "1h15"
 * convertDuration(-2.5) // Returns "-2h30"
 * ```
 */
export const convertDuration = (number: number | string): string => {
  const num = parseFloat(String(number))
  const sign = num >= 0 ? 1 : -1
  const absNum = Math.abs(num)
  const hour = Math.floor(absNum)
  let decpart = absNum - hour
  const min = 1 / 60
  decpart = min * Math.round(decpart / min)
  let minute = `${Math.floor(decpart * 60)}`
  if (minute.length < 2) {
    minute = `0${minute}`
  }
  const signStr = sign === 1 ? '' : '-'
  return `${signStr}${hour}h${minute}`
}

/**
 * Formats full name to short name format
 *
 * Converts full name to "FirstName M. L." format (first name + initials of other names).
 * Useful for displaying abbreviated names in lists or compact views.
 *
 * @param name - Full name string (e.g., "John Michael Smith")
 * @returns Short name format (e.g., "John M. S.")
 * @public
 * @example
 * ```typescript
 * formatName("John Michael Smith") // Returns "John M. S."
 * formatName("Mary Jane Watson") // Returns "Mary J. W."
 * ```
 */
export const formatName = (name: string): string => {
  const arr = name.split(' ').filter((part) => part.length > 0)
  if (arr.length === 0) return ''

  let shortname = arr[0]
  for (let i = 1; i < arr.length; i++) {
    const n = arr[i]
    if (n && n.charAt(0)) {
      shortname = `${shortname} ${n.charAt(0)}.`
    }
  }
  // Add trailing space if there are no initials (single name case)
  if (arr.length === 1) {
    shortname = `${shortname} `
  }
  return shortname
}

/**
 * Capitalizes the first letter of a string
 *
 * Converts first character to uppercase and leaves the rest unchanged.
 * Handles empty strings gracefully by returning empty string.
 *
 * @param string - The string to capitalize
 * @returns String with first letter capitalized, or empty string if input is empty
 * @public
 * @example
 * ```typescript
 * ucFirst("hello") // Returns "Hello"
 * ucFirst("WORLD") // Returns "WORLD"
 * ucFirst("") // Returns ""
 * ```
 */
export const ucFirst = (string: string): string => {
  if (!string) return ''
  return string.charAt(0).toUpperCase() + string.slice(1)
}

/**
 * Converts seconds to formatted hours string
 *
 * Formats seconds as "XhYm" or "XhYmZs" format depending on options.
 * Handles negative values and optional sign prefix.
 * Pads seconds to 2 digits when displayed.
 *
 * @param timeInSeconds - Number of seconds to convert
 * @param displaySeconds - Whether to include seconds in output (default: false)
 * @param addSign - Whether to add "+" prefix for positive values (default: false)
 * @returns Formatted time string (e.g., "2h30m" or "2h30m45s" or "-2h30m")
 * @public
 * @example
 * ```typescript
 * convertSecondsToHours(9000) // Returns "2h30m"
 * convertSecondsToHours(9000, true) // Returns "2h30m00s"
 * convertSecondsToHours(9000, false, true) // Returns "+2h30m"
 * convertSecondsToHours(-9000) // Returns "-2h30m"
 * ```
 */
export const convertSecondsToHours = (timeInSeconds: number | string, displaySeconds = false, addSign = false): string => {
  let sign = addSign ? '+' : ''
  let seconds = typeof timeInSeconds === 'string' ? parseFloat(timeInSeconds) : timeInSeconds

  if (seconds < 0) {
    seconds = Math.abs(seconds)
    sign = '-'
  }

  const pad = (num: number, size: number): string => {
    return `000${num}`.slice(size * -1)
  }

  const time = parseFloat(String(seconds)).toFixed(3)
  const hours = Math.floor(parseFloat(time) / 60 / 60)
  const minutes = Math.floor(parseFloat(time) / 60) % 60
  const secs = Math.floor(parseFloat(time) - minutes * 60)

  return sign + (hours > 0 ? `${hours}h` : '') + (minutes >= 0 ? minutes : '') + (displaySeconds ? `m${pad(secs, 2)}s` : 'm')
}

/**
 * Generates initials from a full name, handling complex last names with hyphens and spaces
 *
 * Matches the backend logic for consistent name processing across the application.
 * Extracts first letter of first name and first letter of each word in last name (including hyphenated names).
 * Returns empty string for empty or whitespace-only input.
 *
 * @param name - Full name string (e.g., "John Smith-Jones")
 * @returns Formatted initials string (e.g., "J.S.J.") or empty string if input is invalid
 * @public
 * @example
 * ```typescript
 * getInitials("John Smith") // Returns "J.S."
 * getInitials("Mary Jane Watson-Parker") // Returns "M.J.W.P."
 * getInitials("Jean-Pierre Dupont") // Returns "J.P.D."
 * getInitials("") // Returns ""
 * ```
 */
export const getInitials = (name: string): string => {
  if (!name || name.trim() === '') {
    return ''
  }

  const trimmedName = name.trim()
  const nameParts = trimmedName.split(' ')

  if (nameParts.length === 0) {
    return ''
  }

  // Handle first name - split by hyphens too to get all initials
  const firstNameParts = nameParts[0].split(/[\s-]+/)
  const firstNameInitials = firstNameParts
    .filter((part) => part.length > 0)
    .map((part) => part[0]?.toUpperCase() || '')
    .filter((initial) => initial.length > 0)

  // Handle last name parts (everything after first name)
  const lastNameParts = nameParts.slice(1).join(' ')

  if (!lastNameParts) {
    return firstNameInitials.join('')
  }

  // Split by spaces, hyphens, and other word separators (same as backend logic)
  const lastNameSegments = lastNameParts
    .split(/[\s-]+/)
    .filter((part) => part.length > 0) // Remove empty strings
    .map((part) => part[0]?.toUpperCase() || '')
    .filter((initial) => initial.length > 0) // Remove empty initials

  // Combine first name initials with last name initials
  const allInitials = [...firstNameInitials, ...lastNameSegments]

  return allInitials.join('')
}
